import {
  base64UrlDecode,
  base64UrlEncode,
  constantTimeEqual,
  hashForDomain,
  randomBytes,
  serializeForDomain,
} from "./encoding.js";
import {
  asRecord,
  assertExactKeys,
  assertHttpsOrigin,
  assertLiteral,
  assertSafeIdentifier,
  fail,
  readArray,
  readInteger,
  readString,
} from "./validation.js";

export const DEVICE_SIGNING_ALGORITHM = "ECDSA-P256-SHA256" as const;
export const DEVICE_WRAPPING_ALGORITHM = "RSA-OAEP-3072-SHA256" as const;
export const ES256_SIGNATURE_BYTES = 64;
export const WRAPPING_CHALLENGE_BYTES = 32;
export type RelationshipPermission = "open" | "lock";

export interface P256PublicJwk {
  readonly kty: "EC";
  readonly crv: "P-256";
  readonly x: string;
  readonly y: string;
}

export interface Rsa3072PublicJwk {
  readonly kty: "RSA";
  readonly n: string;
  readonly e: "AQAB";
}

export interface DevicePublicKeys {
  readonly deviceKeyId: string;
  readonly signingAlgorithm: typeof DEVICE_SIGNING_ALGORITHM;
  readonly signingPublicKey: P256PublicJwk;
  readonly wrappingAlgorithm: typeof DEVICE_WRAPPING_ALGORITHM;
  readonly wrappingPublicKey: Rsa3072PublicJwk;
}

export interface DeviceKeyPairs {
  readonly publicKeys: DevicePublicKeys;
  readonly signingKeyPair: CryptoKeyPair;
  readonly wrappingKeyPair: CryptoKeyPair;
}

export interface IssuerSigningKeyPair {
  readonly issuerKeyId: string;
  readonly publicKey: P256PublicJwk;
  readonly keyPair: CryptoKeyPair;
}

export interface EnrollmentTranscript {
  readonly version: 1;
  readonly invitationId: string;
  readonly relationshipId: string;
  readonly audience: string;
  readonly relyingOrganizationId: string;
  readonly partyId: string;
  readonly partyRole: "owner" | "participant";
  readonly purpose: string;
  readonly scope: string;
  readonly permissions: readonly RelationshipPermission[];
  readonly challengeId: string;
  readonly challenge: string;
  readonly issuedAtMs: number;
  readonly expiresAtMs: number;
  readonly device: DevicePublicKeys;
}

export interface EnrollmentSignatureProof {
  readonly transcript: EnrollmentTranscript;
  readonly signature: string;
}

export interface WrappingChallengeContext {
  readonly version: 1;
  readonly challengeId: string;
  readonly invitationId: string;
  readonly relationshipId: string;
  readonly audience: string;
  readonly deviceKeyId: string;
}

export interface EncryptedWrappingChallenge {
  readonly context: WrappingChallengeContext;
  readonly ciphertext: string;
}

export interface IssuedWrappingChallenge {
  readonly challenge: EncryptedWrappingChallenge;
  readonly expectedResponse: string;
}

export async function generateDeviceKeyPairs(): Promise<DeviceKeyPairs> {
  const [signingKeyPair, wrappingKeyPair] = await Promise.all([
    crypto.subtle.generateKey(
      { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["sign", "verify"],
    ),
    crypto.subtle.generateKey(
      {
        name: "RSA-OAEP",
        modulusLength: 3072,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: "SHA-256",
      },
      false,
      ["encrypt", "decrypt"],
    ),
  ]);

  assertGeneratedPrivateKey(signingKeyPair.privateKey, "ECDSA", "sign");
  assertGeneratedPrivateKey(wrappingKeyPair.privateKey, "RSA-OAEP", "decrypt");

  return {
    signingKeyPair,
    wrappingKeyPair,
    publicKeys: await normalizeDevicePublicKeys(
      signingKeyPair.publicKey,
      wrappingKeyPair.publicKey,
    ),
  };
}

export async function generateIssuerSigningKeyPair(): Promise<IssuerSigningKeyPair> {
  const keyPair = await crypto.subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["sign", "verify"],
  );
  assertGeneratedPrivateKey(keyPair.privateKey, "ECDSA", "sign");
  const publicKey = await exportP256PublicKey(keyPair.publicKey);
  return {
    issuerKeyId: await issuerPublicKeyId(publicKey),
    publicKey,
    keyPair,
  };
}

export async function normalizeDevicePublicKeys(
  signingPublicKey: CryptoKey,
  wrappingPublicKey: CryptoKey,
): Promise<DevicePublicKeys> {
  const signing = await exportP256PublicKey(signingPublicKey);
  const wrapping = await exportRsa3072PublicKey(wrappingPublicKey);
  return makeDevicePublicKeys(signing, wrapping);
}

export async function makeDevicePublicKeys(
  signingPublicKey: P256PublicJwk,
  wrappingPublicKey: Rsa3072PublicJwk,
): Promise<DevicePublicKeys> {
  const signing = normalizeP256PublicJwk(signingPublicKey);
  const wrapping = normalizeRsa3072PublicJwk(wrappingPublicKey);
  const keyMaterial = {
    signingAlgorithm: DEVICE_SIGNING_ALGORITHM,
    signingPublicKey: signing,
    wrappingAlgorithm: DEVICE_WRAPPING_ALGORITHM,
    wrappingPublicKey: wrapping,
  } as const;

  return {
    deviceKeyId: await hashForDomain("device-public-keys", keyMaterial),
    ...keyMaterial,
  };
}

export async function validateDevicePublicKeys(value: unknown): Promise<DevicePublicKeys> {
  const record = asRecord(value, "device public keys");
  assertExactKeys(
    record,
    [
      "deviceKeyId",
      "signingAlgorithm",
      "signingPublicKey",
      "wrappingAlgorithm",
      "wrappingPublicKey",
    ],
    "device public keys",
  );
  const deviceKeyId = readString(record, "deviceKeyId", "device public keys", 64);
  assertLiteral(
    record.signingAlgorithm,
    DEVICE_SIGNING_ALGORITHM,
    "device public keys.signingAlgorithm",
  );
  assertLiteral(
    record.wrappingAlgorithm,
    DEVICE_WRAPPING_ALGORITHM,
    "device public keys.wrappingAlgorithm",
  );
  const normalized = await makeDevicePublicKeys(
    normalizeP256PublicJwk(record.signingPublicKey),
    normalizeRsa3072PublicJwk(record.wrappingPublicKey),
  );
  if (normalized.deviceKeyId !== deviceKeyId) {
    fail("KEY_ID_MISMATCH", "Device key identifier does not match its public keys");
  }
  return normalized;
}

export function normalizeP256PublicJwk(value: unknown): P256PublicJwk {
  const record = asRecord(value, "P-256 public key");
  const kty = readString(record, "kty", "P-256 public key", 8);
  const crv = readString(record, "crv", "P-256 public key", 16);
  const x = readString(record, "x", "P-256 public key", 64);
  const y = readString(record, "y", "P-256 public key", 64);
  if (kty !== "EC" || crv !== "P-256") {
    return fail("INVALID_KEY", "Expected a P-256 public key");
  }
  if (base64UrlDecode(x, 32).byteLength !== 32 || base64UrlDecode(y, 32).byteLength !== 32) {
    return fail("INVALID_KEY", "P-256 coordinates must be 32 bytes");
  }
  return { kty: "EC", crv: "P-256", x, y };
}

export function normalizeRsa3072PublicJwk(value: unknown): Rsa3072PublicJwk {
  const record = asRecord(value, "RSA public key");
  const kty = readString(record, "kty", "RSA public key", 8);
  const n = readString(record, "n", "RSA public key", 1024);
  const e = readString(record, "e", "RSA public key", 16);
  if (kty !== "RSA" || e !== "AQAB" || base64UrlDecode(n, 384).byteLength !== 384) {
    return fail("INVALID_KEY", "Expected a 3072-bit RSA public key with exponent 65537");
  }
  return { kty: "RSA", n, e: "AQAB" };
}

export async function importP256VerificationKey(value: P256PublicJwk): Promise<CryptoKey> {
  const normalized = normalizeP256PublicJwk(value);
  try {
    return await crypto.subtle.importKey(
      "jwk",
      { ...normalized, ext: true, key_ops: ["verify"] },
      { name: "ECDSA", namedCurve: "P-256" },
      true,
      ["verify"],
    );
  } catch {
    return fail("INVALID_KEY", "P-256 public key import failed");
  }
}

export async function importRsaWrappingPublicKey(value: Rsa3072PublicJwk): Promise<CryptoKey> {
  const normalized = normalizeRsa3072PublicJwk(value);
  try {
    return await crypto.subtle.importKey(
      "jwk",
      { ...normalized, alg: "RSA-OAEP-256", ext: true, key_ops: ["encrypt"] },
      { name: "RSA-OAEP", hash: "SHA-256" },
      true,
      ["encrypt"],
    );
  } catch {
    return fail("INVALID_KEY", "RSA public key import failed");
  }
}

export async function issuerPublicKeyId(value: P256PublicJwk): Promise<string> {
  return hashForDomain("issuer-public-key", normalizeP256PublicJwk(value));
}

export async function signEs256(
  privateKey: CryptoKey,
  domain: string,
  payload: Parameters<typeof serializeForDomain>[1],
): Promise<string> {
  assertKey(privateKey, "private", "ECDSA", "sign");
  const signature = new Uint8Array(
    await crypto.subtle.sign(
      { name: "ECDSA", hash: "SHA-256" },
      privateKey,
      serializeForDomain(domain, payload),
    ),
  );
  if (signature.byteLength !== ES256_SIGNATURE_BYTES) {
    return fail("CRYPTO_FAILURE", "Unexpected ES256 signature encoding");
  }
  return base64UrlEncode(signature);
}

export async function verifyEs256(
  publicKey: CryptoKey,
  domain: string,
  payload: Parameters<typeof serializeForDomain>[1],
  encodedSignature: string,
): Promise<boolean> {
  assertKey(publicKey, "public", "ECDSA", "verify");
  let signature: Uint8Array<ArrayBuffer>;
  try {
    signature = base64UrlDecode(encodedSignature, ES256_SIGNATURE_BYTES);
  } catch {
    return false;
  }
  if (signature.byteLength !== ES256_SIGNATURE_BYTES) {
    return false;
  }
  try {
    return await crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      publicKey,
      signature,
      serializeForDomain(domain, payload),
    );
  } catch {
    return false;
  }
}

export async function signEnrollmentTranscript(
  transcriptInput: EnrollmentTranscript,
  signingPrivateKey: CryptoKey,
): Promise<EnrollmentSignatureProof> {
  const transcript = await normalizeEnrollmentTranscript(transcriptInput);
  return {
    transcript,
    signature: await signEs256(
      signingPrivateKey,
      "device-enrollment-transcript",
      transcript,
    ),
  };
}

export async function verifyEnrollmentTranscript(
  proofInput: EnrollmentSignatureProof,
  nowMs: number,
): Promise<EnrollmentTranscript> {
  const proof = asRecord(proofInput, "enrollment proof");
  assertExactKeys(proof, ["transcript", "signature"], "enrollment proof");
  const transcript = await normalizeEnrollmentTranscript(proof.transcript);
  const signature = readString(proof, "signature", "enrollment proof", 128);
  if (!Number.isSafeInteger(nowMs) || nowMs < transcript.issuedAtMs || nowMs >= transcript.expiresAtMs) {
    return fail("ENROLLMENT_NOT_CURRENT", "Enrollment transcript is not current");
  }
  const publicKey = await importP256VerificationKey(transcript.device.signingPublicKey);
  if (
    !(await verifyEs256(
      publicKey,
      "device-enrollment-transcript",
      transcript,
      signature,
    ))
  ) {
    return fail("INVALID_ENROLLMENT_SIGNATURE", "Enrollment signature is invalid");
  }
  return transcript;
}

export async function issueWrappingKeyChallenge(
  wrappingPublicKey: Rsa3072PublicJwk,
  contextInput: WrappingChallengeContext,
  responseBytes: Uint8Array<ArrayBuffer> = randomBytes(WRAPPING_CHALLENGE_BYTES),
): Promise<IssuedWrappingChallenge> {
  const context = normalizeWrappingChallengeContext(contextInput);
  if (responseBytes.byteLength !== WRAPPING_CHALLENGE_BYTES) {
    return fail("INVALID_CHALLENGE", "Wrapping challenge response must be 32 bytes");
  }
  const publicKey = await importRsaWrappingPublicKey(wrappingPublicKey);
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: "RSA-OAEP",
      label: serializeForDomain("wrapping-possession-challenge-label", context),
    },
    publicKey,
    responseBytes,
  );
  return {
    challenge: { context, ciphertext: base64UrlEncode(ciphertext) },
    expectedResponse: base64UrlEncode(responseBytes),
  };
}

export async function answerWrappingKeyChallenge(
  wrappingPrivateKey: CryptoKey,
  challengeInput: EncryptedWrappingChallenge,
): Promise<string> {
  assertKey(wrappingPrivateKey, "private", "RSA-OAEP", "decrypt");
  const challenge = normalizeEncryptedWrappingChallenge(challengeInput);
  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt(
      {
        name: "RSA-OAEP",
        label: serializeForDomain(
          "wrapping-possession-challenge-label",
          challenge.context,
        ),
      },
      wrappingPrivateKey,
      base64UrlDecode(challenge.ciphertext, 384),
    );
  } catch {
    return fail("INVALID_WRAPPING_PROOF", "Wrapping challenge could not be answered");
  }
  const bytes = new Uint8Array(plaintext);
  if (bytes.byteLength !== WRAPPING_CHALLENGE_BYTES) {
    return fail("INVALID_WRAPPING_PROOF", "Wrapping challenge response is invalid");
  }
  return base64UrlEncode(bytes);
}

export function verifyWrappingKeyChallenge(
  issued: IssuedWrappingChallenge,
  response: string,
): boolean {
  try {
    return constantTimeEqual(
      base64UrlDecode(issued.expectedResponse, WRAPPING_CHALLENGE_BYTES),
      base64UrlDecode(response, WRAPPING_CHALLENGE_BYTES),
    );
  } catch {
    return false;
  }
}

export async function assertDeviceKeyPairsMatch(
  keys: DeviceKeyPairs,
  expectedDeviceKeyId: string,
): Promise<void> {
  assertKey(keys.signingKeyPair.privateKey, "private", "ECDSA", "sign");
  assertKey(keys.wrappingKeyPair.privateKey, "private", "RSA-OAEP", "decrypt");
  const normalized = await normalizeDevicePublicKeys(
    keys.signingKeyPair.publicKey,
    keys.wrappingKeyPair.publicKey,
  );
  if (
    normalized.deviceKeyId !== expectedDeviceKeyId ||
    keys.publicKeys.deviceKeyId !== expectedDeviceKeyId
  ) {
    fail("DEVICE_KEY_MISMATCH", "Device private keys do not match the card device");
  }
}

async function exportP256PublicKey(key: CryptoKey): Promise<P256PublicJwk> {
  assertKey(key, "public", "ECDSA", "verify");
  return normalizeP256PublicJwk(await crypto.subtle.exportKey("jwk", key));
}

async function exportRsa3072PublicKey(key: CryptoKey): Promise<Rsa3072PublicJwk> {
  assertKey(key, "public", "RSA-OAEP", "encrypt");
  return normalizeRsa3072PublicJwk(await crypto.subtle.exportKey("jwk", key));
}

async function normalizeEnrollmentTranscript(value: unknown): Promise<EnrollmentTranscript> {
  const record = asRecord(value, "enrollment transcript");
  assertExactKeys(
    record,
    [
      "version",
      "invitationId",
      "relationshipId",
      "audience",
      "relyingOrganizationId",
      "partyId",
      "partyRole",
      "purpose",
      "scope",
      "permissions",
      "challengeId",
      "challenge",
      "issuedAtMs",
      "expiresAtMs",
      "device",
    ],
    "enrollment transcript",
  );
  assertLiteral(record.version, 1, "enrollment transcript.version");
  const invitationId = readString(record, "invitationId", "enrollment transcript", 256);
  const relationshipId = readString(record, "relationshipId", "enrollment transcript", 256);
  const audience = readString(record, "audience", "enrollment transcript", 512);
  const relyingOrganizationId = readString(
    record,
    "relyingOrganizationId",
    "enrollment transcript",
    256,
  );
  const partyId = readString(record, "partyId", "enrollment transcript", 256);
  const partyRole = readString(record, "partyRole", "enrollment transcript", 32);
  const purpose = readString(record, "purpose", "enrollment transcript", 1024);
  const scope = readString(record, "scope", "enrollment transcript", 256);
  const permissions = normalizeRelationshipPermissions(
    readArray(record, "permissions", "enrollment transcript", 2),
    "enrollment transcript.permissions",
  );
  const challengeId = readString(record, "challengeId", "enrollment transcript", 256);
  const challenge = readString(record, "challenge", "enrollment transcript", 128);
  const issuedAtMs = readInteger(record, "issuedAtMs", "enrollment transcript", 0);
  const expiresAtMs = readInteger(record, "expiresAtMs", "enrollment transcript", 1);
  assertSafeIdentifier(invitationId, "enrollment transcript.invitationId");
  assertSafeIdentifier(relationshipId, "enrollment transcript.relationshipId");
  assertSafeIdentifier(
    relyingOrganizationId,
    "enrollment transcript.relyingOrganizationId",
  );
  assertSafeIdentifier(partyId, "enrollment transcript.partyId");
  if (partyRole !== "owner" && partyRole !== "participant") {
    return fail("INVALID_FORMAT", "Enrollment party role is invalid");
  }
  assertSafeIdentifier(challengeId, "enrollment transcript.challengeId");
  assertHttpsOrigin(audience, "enrollment transcript.audience");
  if (base64UrlDecode(challenge, 64).byteLength < 32 || issuedAtMs >= expiresAtMs) {
    return fail("INVALID_FORMAT", "Enrollment challenge or validity window is invalid");
  }
  return {
    version: 1,
    invitationId,
    relationshipId,
    audience,
    relyingOrganizationId,
    partyId,
    partyRole,
    purpose,
    scope,
    permissions,
    challengeId,
    challenge,
    issuedAtMs,
    expiresAtMs,
    device: await validateDevicePublicKeys(record.device),
  };
}

function normalizeRelationshipPermissions(
  values: readonly unknown[],
  label: string,
): readonly RelationshipPermission[] {
  if (values.length === 0) {
    return fail("INVALID_FORMAT", `${label} must not be empty`);
  }
  const permissions = new Set<RelationshipPermission>();
  for (const value of values) {
    if (value !== "open" && value !== "lock") {
      return fail("INVALID_FORMAT", `${label} contains an unsupported permission`);
    }
    if (permissions.has(value)) {
      return fail("INVALID_FORMAT", `${label} contains a duplicate permission`);
    }
    permissions.add(value);
  }
  return (["open", "lock"] as const).filter((permission) => permissions.has(permission));
}

function normalizeWrappingChallengeContext(value: unknown): WrappingChallengeContext {
  const record = asRecord(value, "wrapping challenge context");
  assertExactKeys(
    record,
    ["version", "challengeId", "invitationId", "relationshipId", "audience", "deviceKeyId"],
    "wrapping challenge context",
  );
  assertLiteral(record.version, 1, "wrapping challenge context.version");
  const challengeId = readString(record, "challengeId", "wrapping challenge context", 256);
  const invitationId = readString(record, "invitationId", "wrapping challenge context", 256);
  const relationshipId = readString(record, "relationshipId", "wrapping challenge context", 256);
  const audience = readString(record, "audience", "wrapping challenge context", 512);
  const deviceKeyId = readString(record, "deviceKeyId", "wrapping challenge context", 64);
  assertSafeIdentifier(challengeId, "wrapping challenge context.challengeId");
  assertSafeIdentifier(invitationId, "wrapping challenge context.invitationId");
  assertSafeIdentifier(relationshipId, "wrapping challenge context.relationshipId");
  assertHttpsOrigin(audience, "wrapping challenge context.audience");
  if (base64UrlDecode(deviceKeyId, 32).byteLength !== 32) {
    return fail("INVALID_FORMAT", "Wrapping challenge device key identifier is invalid");
  }
  return { version: 1, challengeId, invitationId, relationshipId, audience, deviceKeyId };
}

function normalizeEncryptedWrappingChallenge(value: unknown): EncryptedWrappingChallenge {
  const record = asRecord(value, "encrypted wrapping challenge");
  assertExactKeys(record, ["context", "ciphertext"], "encrypted wrapping challenge");
  const ciphertext = readString(record, "ciphertext", "encrypted wrapping challenge", 1024);
  if (base64UrlDecode(ciphertext, 384).byteLength !== 384) {
    return fail("INVALID_CHALLENGE", "Encrypted wrapping challenge has the wrong size");
  }
  return {
    context: normalizeWrappingChallengeContext(record.context),
    ciphertext,
  };
}

function assertGeneratedPrivateKey(
  key: CryptoKey,
  algorithm: string,
  usage: KeyUsage,
): void {
  assertKey(key, "private", algorithm, usage);
  if (key.extractable) {
    fail("CRYPTO_FAILURE", "Generated device private key must be non-extractable");
  }
}

function assertKey(
  key: CryptoKey,
  type: KeyType,
  algorithm: string,
  usage: KeyUsage,
): void {
  if (
    key.type !== type ||
    key.algorithm.name !== algorithm ||
    !key.usages.includes(usage)
  ) {
    fail("INVALID_KEY", `Expected a ${algorithm} ${type} key usable for ${usage}`);
  }

  if (algorithm === "ECDSA") {
    const ec = key.algorithm as EcKeyAlgorithm;
    if (ec.namedCurve !== "P-256") {
      fail("INVALID_KEY", "Expected the P-256 curve");
    }
  }

  if (algorithm === "RSA-OAEP") {
    const rsa = key.algorithm as RsaHashedKeyAlgorithm;
    if (rsa.modulusLength !== 3072 || rsa.hash.name !== "SHA-256") {
      fail("INVALID_KEY", "Expected RSA-OAEP-3072 with SHA-256");
    }
  }
}
